import { BaseTheme } from 'design-system';
import { useCallback, useEffect, useState } from 'react';
import type { SearchSuggestion } from './SearchBar.types';

export const BROWSE_ALL_INITIAL_COUNT = 12;
export const MAX_RECENT_CHIPS = 3;
export const DEFAULT_SEARCHBAR_WIDTH = '280px';

export const dropdownChipSx = {
  height: '28px',
  '& .MuiChip-label': { fontWeight: 600 },
  '&.MuiChip-outlined': { borderWidth: '1px' },
};

export interface RecentChip {
  label: string;
  value: string;
}

export type ChipItem = string | SearchSuggestion;

export const getChipLabel = (item: ChipItem): string => (typeof item === 'string' ? item : item.label);

export const getChipValue = (item: ChipItem): string => (typeof item === 'string' ? item : item.value);

export const isSuggestionList = (data: ChipItem[]): data is SearchSuggestion[] =>
  data.length > 0 && typeof data[0] !== 'string';

export const filterChipsByQuery = <T extends ChipItem>(items: T[], query: string): T[] => {
  if (query.length === 0) return items;
  const lowered = query.toLowerCase();
  return items.filter((item) => getChipLabel(item).toLowerCase().includes(lowered));
};

export const dedupeChipsByRecents = <T extends ChipItem>(items: T[], recents: RecentChip[]): T[] => {
  if (recents.length === 0) return items;
  const recentValues = new Set(recents.map((r) => r.value));
  return items.filter((item) => !recentValues.has(getChipValue(item)));
};

export const findChipItem = (data: ChipItem[], value: string): ChipItem | undefined =>
  data.find((item) => getChipValue(item) === value);

const isRecentChip = (entry: unknown): entry is RecentChip =>
  !!entry &&
  typeof entry === 'object' &&
  typeof (entry as RecentChip).label === 'string' &&
  typeof (entry as RecentChip).value === 'string';

export const readRecents = (storageKey: string | undefined): RecentChip[] => {
  if (!storageKey || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentChip).slice(0, MAX_RECENT_CHIPS);
  } catch {
    return [];
  }
};

export const writeRecents = (storageKey: string | undefined, entries: RecentChip[]): void => {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(entries));
  } catch {
    // noop
  }
};

export const pushRecentEntry = (current: RecentChip[], entry: RecentChip): RecentChip[] =>
  [entry, ...current.filter((r) => r.value !== entry.value)].slice(0, MAX_RECENT_CHIPS);

export const buildBrowseButtonLabel = (totalMatchCount: number, trimmedInput: string): string =>
  trimmedInput.length > 0
    ? `Show all ${totalMatchCount} matches for "${trimmedInput}"`
    : `Browse all ${totalMatchCount} categories`;

export const sectionHeadingSx = (theme: BaseTheme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.25, 1.5, 0.5, 1.5),
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  fontSize: '0.6875rem',
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.secondary,
});

export const chipContainerStyle = (theme: BaseTheme) => ({
  padding: theme.spacing(1),
  display: 'flex',
  borderRadius: theme.spacing(0, 0, 3, 3),
});

export interface UseRecentChipsResult {
  recents: RecentChip[];
  pushRecent: (entry: RecentChip) => void;
}

export const useRecentChips = (storageKey: string | undefined): UseRecentChipsResult => {
  const [recents, setRecents] = useState<RecentChip[]>(() => readRecents(storageKey));

  useEffect(() => {
    setRecents(readRecents(storageKey));
  }, [storageKey]);

  const pushRecent = useCallback(
    (entry: RecentChip) => {
      if (!storageKey || !entry.value) return;
      setRecents((prev) => {
        const next = pushRecentEntry(prev, entry);
        writeRecents(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  return { recents, pushRecent };
};
